/* Inventory — STORY-028 and STORY-030.
 *
 * ── two tabs, because they answer two questions ─────────────────────────────
 * "What do we own" and "what has not come back" are different jobs, and the
 * second is the one the whole module exists for. It goes first.
 *
 * ── condition is never colour alone ─────────────────────────────────────────
 * The workbook used markers with no text. Every row says its condition in
 * words, and the two that mean somebody should look at the thing are said
 * again as a badge rather than only tinted.
 *
 * ── a just-returned row lingers, then leaves ────────────────────────────────
 * The list is "what has not come back", so a row stops qualifying the instant
 * it is tapped — but it has to stay long enough to carry its condition
 * question, or that screen could never capture one while the loadout panel
 * could. Found 2026-08-11 by marking one item returned from three places and
 * getting three different outcomes.
 */

import { type Assignment, ATTENTION_CONDITIONS, type InventoryItem } from '@eventuley/contracts';
import { formatMoney, money, parseCurrency, todayIn } from '@eventuley/domain';
import { formatDate } from '@eventuley/i18n';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, Tabs } from '../../../shared/ui/index.js';
import { useEnabledValues, useMembers } from '../../organisations/index.js';
import { useDeleteItem, useInventory, useOutstanding } from '../api/index.js';
import { ItemForm } from '../components/item-form.js';
import { CONDITION_LABELS, ReturnControl } from '../components/return-control.js';
import { useReturnHolding } from '../components/use-return-holding.js';

export function InventoryScreen() {
  const t = useTranslate();
  const { organisationId } = useParams({ strict: false }) as { organisationId: string };
  const [tab, setTab] = useState('outstanding');

  return (
    <>
      <div className="page-head">
        <h1>{t('inventory.title')}</h1>
      </div>
      <Tabs
        label={t('inventory.tabs_label')}
        selected={tab}
        onSelect={setTab}
        tabs={[
          {
            /* First, and selected. This is the question the module exists to
               answer and the one the reference workbook never did. */
            id: 'outstanding',
            label: t('inventory.tab_outstanding'),
            panel: <OutstandingPanel organisationId={organisationId} />,
          },
          {
            id: 'items',
            label: t('inventory.tab_items'),
            panel: <ItemsPanel organisationId={organisationId} />,
          },
        ]}
      />
    </>
  );
}

function OutstandingPanel({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();

  /* The reader's zone decides which side of the split a row falls on. Only the
   * client knows where the reader is standing. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data, error, isLoading } = useOutstanding(organisationId, timeZone);

  /* Rows the reader has just marked returned. The server has already dropped
   * them; they stay on screen until their follow-up is answered or dismissed,
   * and they are NOT counted — a row waiting on an optional question is not
   * missing equipment and must not inflate the number this screen reports.
   *
   * The rule lives in the hook, shared with the Event's loadout, because
   * sharing the control alone was twice not enough. */
  const holding = useReturnHolding();

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  if (isLoading && data === undefined) {
    return <p className="field__hint">{t('common.loading')}</p>;
  }

  /* Whatever the server still calls outstanding, minus anything already being
   * held on screen — otherwise a row would appear twice between the write and
   * the refetch. */
  const outstanding = (data?.outstanding ?? []).filter((row) => !holding.isHeld(row.id));
  const packing = data?.packing ?? [];

  const daysOut = (assignment: Assignment) => {
    if (assignment.event.date === null) return null;
    const today = todayIn(timeZone, new Date());
    const from = Date.parse(`${assignment.event.date}T00:00:00Z`);
    const to = Date.parse(`${today}T00:00:00Z`);
    return Math.max(0, Math.round((to - from) / 86_400_000));
  };

  return (
    <>
      <p className="field__hint" role="status">
        {/* The count, said plainly. It never includes the packing list or a row
            waiting on its follow-up. */}
        {t('inventory.outstanding_count', { count: data?.outstandingCount ?? 0 })}
      </p>

      {outstanding.length === 0 && holding.held.length === 0 ? (
        <Callout tone="info">{t('inventory.nothing_out')}</Callout>
      ) : (
        <ul className="row-list">
          {outstanding.map((assignment) => (
            <li className="row" key={assignment.id}>
              <span className="row__main">
                <span className="row__title">{assignment.item.name}</span>
                <span className="row__meta">
                  {assignment.event.date === null
                    ? t('inventory.out_for_undated', { event: assignment.event.name })
                    : t('inventory.out_for', {
                        event: assignment.event.name,
                        date: formatDate(new Date(`${assignment.event.date}T00:00:00Z`), {
                          locale,
                          timeZone: 'UTC',
                        }),
                        days: daysOut(assignment) ?? 0,
                      })}
                </span>
              </span>
              <span className="row__actions">
                <ReturnControl
                  organisationId={organisationId}
                  assignmentIds={[assignment.id]}
                  label={t('inventory.mark_returned')}
                  onReturned={() => holding.hold(assignment)}
                  onDone={() => holding.release(assignment.id)}
                />
              </span>
            </li>
          ))}

          {/* Just returned, and still carrying its question. It leaves the
              moment that is answered or dismissed. */}
          {holding.held.map((assignment) => (
            <li className="row" key={`held-${assignment.id}`}>
              <span className="row__main">
                <span className="row__title">{assignment.item.name}</span>
                <span className="row__meta">{t('inventory.just_returned')}</span>
              </span>
              <span className="row__actions">
                {/* Already asking: this instance replaced the one that recorded
                    the return, and the question has to survive that. */}
                <ReturnControl
                  organisationId={organisationId}
                  assignmentIds={[assignment.id]}
                  label={t('inventory.mark_returned')}
                  startAsking
                  onDone={() => holding.release(assignment.id)}
                />
              </span>
            </li>
          ))}
        </ul>
      )}

      {packing.length > 0 ? (
        <section className="settings__section" aria-labelledby="packing-heading">
          <h2 id="packing-heading">{t('inventory.packing_heading')}</h2>
          {/* Separate, and excluded from the count above. Filing next month's
              loadout under things to chase makes that number wrong. */}
          <p className="field__hint">{t('inventory.packing_hint')}</p>
          <ul className="row-list">
            {packing.map((assignment) => (
              <li className="row" key={assignment.id}>
                <span className="row__main">
                  <span className="row__title">{assignment.item.name}</span>
                  <span className="row__meta">
                    {t('inventory.packing_for', { event: assignment.event.name })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function ItemsPanel({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { data, error, isLoading } = useInventory(organisationId);
  const { data: memberData } = useMembers(organisationId);
  const { data: enabled } = useEnabledValues(organisationId);
  const [adding, setAdding] = useState(false);
  /* Which item is open for editing, by id. One at a time — two open forms
   * writing to the same list is a state nobody asked for. */
  const [editing, setEditing] = useState<string | null>(null);

  const currencies = (enabled?.values ?? [])
    .filter((value) => value.kind === 'currency')
    .map((value) => value.value);

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  const items = data?.items ?? [];

  return (
    <>
      <div className="filters">
        <Button variant="primary" onClick={() => setAdding(!adding)}>
          {adding ? t('inventory.close_form') : t('inventory.add')}
        </Button>
      </div>

      {adding ? (
        <ItemForm
          organisationId={organisationId}
          members={memberData?.members ?? []}
          currencies={currencies}
          onDone={() => setAdding(false)}
        />
      ) : null}

      {isLoading && items.length === 0 ? (
        <p className="field__hint">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <Callout tone="info">{t('inventory.nothing_yet')}</Callout>
      ) : (
        <ul className="row-list">
          {items.map((item) => (
            <ItemRow
              key={item.id}
              organisationId={organisationId}
              item={item}
              locale={locale}
              members={memberData?.members ?? []}
              currencies={currencies}
              editing={editing === item.id}
              onEdit={() => setEditing(editing === item.id ? null : item.id)}
              onEdited={() => setEditing(null)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

function ItemRow({
  organisationId,
  item,
  locale,
  members,
  currencies,
  editing,
  onEdit,
  onEdited,
}: {
  readonly organisationId: string;
  readonly item: InventoryItem;
  readonly locale: string;
  readonly members: readonly { userId: string; displayName: string }[];
  readonly currencies: readonly string[];
  readonly editing: boolean;
  readonly onEdit: () => void;
  readonly onEdited: () => void;
}) {
  const t = useTranslate();
  const remove = useDeleteItem(organisationId);
  const [confirming, setConfirming] = useState(false);

  const cost =
    item.costMinor === null || item.costCurrency === null
      ? null
      : (() => {
          const code = parseCurrency(item.costCurrency);
          return code
            ? formatMoney(money(item.costMinor, code), locale)
            : `${item.costMinor} ${item.costCurrency}`;
        })();

  const needsAttention = (ATTENTION_CONDITIONS as readonly string[]).includes(item.condition);
  const inUse = remove.error instanceof ApiError && remove.error.status === 409;

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">
          {item.name}
          {/* Problematic and lost, said as a badge as well as in the line
              below — `design.md` forbids colour alone, and these are the two
              the list exists to make findable at a glance. */}
          {needsAttention ? (
            <span className="badge badge--danger">{t(CONDITION_LABELS[item.condition])}</span>
          ) : null}
        </span>
        <span className="row__meta">
          {t('inventory.row_meta', { condition: t(CONDITION_LABELS[item.condition]) })}
        </span>
        {cost ? (
          <span className="row__meta">
            {item.costFromTransaction
              ? /* Said, because the reader must not go looking for an edit
                   control that is deliberately absent. */
                t('inventory.cost_from_books', { amount: cost })
              : t('inventory.cost', { amount: cost })}
            {item.purchasedOn
              ? ` · ${formatDate(new Date(`${item.purchasedOn}T00:00:00Z`), {
                  locale,
                  timeZone: 'UTC',
                })}`
              : ''}
          </span>
        ) : null}
        {item.notes ? <span className="row__meta">{item.notes}</span> : null}
        {item.holder ? (
          <span className="row__meta">
            {t('inventory.held_by', {
              name: item.holder.anonymised ? t('inventory.former_member') : item.holder.displayName,
            })}
          </span>
        ) : null}
      </span>

      <span className="row__actions">
        <Button variant="ghost" onClick={onEdit}>
          {editing ? t('inventory.close_form') : t('inventory.edit')}
        </Button>
        <Button variant="ghost" onClick={() => setConfirming(true)}>
          {t('inventory.delete')}
        </Button>
      </span>

      {editing ? (
        <div className="row__expand">
          {/* The same form as registering. Two forms that agree today are two
              forms that disagree later. */}
          <ItemForm
            organisationId={organisationId}
            members={members}
            currencies={currencies}
            item={item}
            onDone={onEdited}
          />
        </div>
      ) : null}

      {inUse ? (
        <div className="row__expand">
          {/* And what to do instead — marking it lost keeps the record and its
              cost, which is the whole reason deletion is refused. */}
          <Callout tone="warning">{t('inventory.delete_in_use')}</Callout>
        </div>
      ) : null}

      {confirming ? (
        <div className="row__expand">
          <Callout tone="danger">
            <strong>{t('inventory.delete_confirm_title', { name: item.name })}</strong>
            <p>{t('inventory.delete_confirm_body')}</p>
            <div className="btn-row">
              <Button
                variant="secondary"
                busy={remove.isPending}
                onClick={() => remove.mutate(item.id)}
              >
                {t('inventory.delete_confirm_submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  remove.reset();
                }}
              >
                {t('inventory.delete_confirm_cancel')}
              </Button>
            </div>
          </Callout>
        </div>
      ) : null}
    </li>
  );
}
